import { useEffect, useRef } from 'react';

import type { CreativityType } from '../../../types/banner';
import type { FormDataValue, RoutedFormData } from '../../../types/templates';
import type { TemplateBulkOps } from '../../../types/bulk-ops';
import { getHandlerForTemplate } from '../../../handlers';

interface BulkBridgeParams {
  creativityType: CreativityType;
  formData: RoutedFormData;
  isFormComplete: boolean;
  isSaving: boolean;
  handleFieldChange: (key: string, value: FormDataValue) => Promise<RoutedFormData>;
  handleSaveToLibrary: () => Promise<void>;
}

interface BulkBridgeApi {
  creativityType: CreativityType;
  setFormData: (data: Record<string, FormDataValue>) => Promise<void>;
  save: () => Promise<void>;
  isFormComplete: () => boolean;
  isSaving: () => boolean;
  getFormData: () => RoutedFormData;
  ops: Partial<TemplateBulkOps>;
}

declare global {
  interface Window {
    __bulk?: BulkBridgeApi;
  }
}

export function useBulkEditorBridge(params: BulkBridgeParams): void {
  const ref = useRef(params);
  ref.current = params;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const ops = getHandlerForTemplate(ref.current.creativityType).getBulkOperations?.(ref.current.creativityType) ?? {};

    const api: BulkBridgeApi = {
      get creativityType() {
        return ref.current.creativityType;
      },
      async setFormData(data) {
        for (const [key, value] of Object.entries(data)) {
          await ref.current.handleFieldChange(key, value);
          // useCreativityForm lee formData desde un ref que se refresca recién
          // en el siguiente render. Sin yield, la segunda llamada arranca con
          // state stale y pisa lo que escribió la primera.
          await new Promise(r => setTimeout(r, 50));
        }
        await new Promise(r => setTimeout(r, 400));
      },
      async save() {
        await ref.current.handleSaveToLibrary();
      },
      isFormComplete: () => ref.current.isFormComplete,
      isSaving: () => ref.current.isSaving,
      getFormData: () => ref.current.formData,
      ops
    };

    window.__bulk = api;
    return () => {
      if (window.__bulk === api) delete window.__bulk;
    };
  }, []);
}
